import { EventEmitter, Injectable } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { EntityModalComponent } from "../components/modal/entity-modal.component";
import { ToastrService } from "ngx-toastr";
import { EntityTyp } from "../enums/entity-typ.enum";
import { ActivatedRoute, Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { OwnUserModalComponent } from "app/pages/users/own-user-modal/own-user-modal.component";
import {
  AuthenticationService,
  ClientService,
  EmployeeService,
  HousingService,
  OfficeService,
  ReportService,
  UserService,
} from "app/api/services";
import { UserModalComponent } from "app/pages/users/user-modal/user-modal.component";
import { OfficeModalComponent } from "../../pages/offices/office-modal/office-modal.component";
import { EmployeeModalComponent } from "../../pages/employees/employee-modal/employee-modal.component";
import { HousingModalComponent } from "../../pages/housing/housing-modal/housing-modal.component";
import { ClientModalComponent } from "../../pages/clients/client-modal/client-modal.component";
import { ReportModalComponent } from "../../pages/reports/report-modal/report-modal.component";
import { OptimisticLockingModalComponent } from "../components/optimistic-locking-modal/optimistic-locking-modal.component";
import { ViewModalComponent } from "../../pages/reports/view-modal/view-modal.component";
import { TimeframeModalComponent } from "../../pages/clients/timeframe-modal/timeframe-modal.component";

export interface EntityDto {
  id?: number;

  [key: string]: any;
}

export interface ModalData<Dto extends EntityDto> {
  entity?: Dto;
  openTab?: string | number;

  [key: string]: any;
}

interface ModalStackEntry {
  typ: EntityTyp;
  id?: number;
}

@Injectable({
  providedIn: "root",
})
export class ModalService {
  modalClosed: EventEmitter<void> = new EventEmitter<void>();

  private openModals: Array<ModalStackEntry> = [];

  constructor(
    private modalService: NgbModal,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private officeService: OfficeService,
    private employeeService: EmployeeService,
    private housingService: HousingService,
    private clientService: ClientService,
    private reportService: ReportService
  ) {}

  get hasOpenModals(): boolean {
    return !!this.openModals.length;
  }

  async openOwnUserModal() {
    const user = await firstValueFrom(this.authenticationService.authControllerFindMyUser());
    const modalRef = this.modalService.open(OwnUserModalComponent, {
      windowClass: "modal modal-primary",
      size: "sm",
      backdrop: "static",
    });
    modalRef.componentInstance.entity = user;
    return this.getResult(modalRef);
  }

  openOptimisticLockingModal() {
    this.modalService.open(OptimisticLockingModalComponent, {
      windowClass: "modal modal-primary optimistic-locking-modal",
      size: "sm",
      backdrop: "static",
    });
  }

  async openReportViewModal(filter: any) {
    const modalRef = this.modalService.open(ViewModalComponent, {
      windowClass: "modal modal-primary",
      size: "xl",
      backdrop: "static",
    });
    const component = modalRef.componentInstance;
    this.reportService
      .reportControllerView({ ...filter, limit: Number.MAX_SAFE_INTEGER, page: 0 })
      .subscribe((reports) => {
        component.data = { reports };
      });
  }

  async openTimeframeModal() {
    const modalRef = this.modalService.open(TimeframeModalComponent, {
      windowClass: "modal modal-primary",
      size: "sm",
      backdrop: "static",
    });
    return this.getResult(modalRef);
  }

  async openEntityModal<Dto extends EntityDto>(
    typ: EntityTyp,
    id?: number,
    extraData?: ModalData<Dto>,
    openTab?: string | number
  ): Promise<EntityDto | undefined> {
    if (this.openModals.some((entry) => entry.typ === typ)) {
      this.toastr.warning("Es ist bereits ein Fenster dieses Typs offen.", "Öffnen fehlgeschlagen");
      return;
    }

    const modalConfig = await this.getModalConfig(typ, id);

    const modalRef = this.modalService.open(modalConfig.modalComponent, {
      windowClass: "modal",
      size: modalConfig.size ?? "lg",
      backdrop: "static",
    });

    const data: ModalData<Dto> = { entity: modalConfig.entity as Dto, openTab, ...modalConfig.extraData, ...extraData };

    const component = modalRef.componentInstance as EntityModalComponent<Dto>;
    component.data = data;

    const entry: ModalStackEntry = { typ, id };
    this.openModals.push(entry);
    this.setQueryString(entry);

    const result = await this.getResult(modalRef);
    this.openModals.pop();

    const openModalEntry = this.openModals?.length ? this.openModals[this.openModals.length - 1] : null;
    this.setQueryString(openModalEntry);
    return result;
  }

  private async getResult(modalRef: NgbModalRef) {
    let result;
    try {
      result = await modalRef.result;
    } catch (error) {
      result = null;
    }
    this.modalClosed.emit();
    return result;
  }

  private setQueryString(entry?: ModalStackEntry | null) {
    const params = this.route.snapshot.queryParams;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ...params, modalTyp: entry?.typ ?? null, modalEntityId: entry?.id ?? null },
    });
  }

  private async getModalConfig(
    typ: EntityTyp,
    id?: number
  ): Promise<{
    modalComponent: any;
    entity: EntityDto;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    extraData?: any;
  }> {
    switch (typ) {
      case EntityTyp.User:
        const userEntity = id ? await firstValueFrom(this.userService.userControllerFindOne({ id })) : null;
        return {
          modalComponent: UserModalComponent,
          entity: userEntity,
          size: "md",
          extraData: {
            offices: await firstValueFrom(
              this.officeService.officeControllerFindAllDropdown({
                sIds: userEntity?.officeIds ? [...userEntity?.officeIds] : [],
                initial: true,
              })
            ),
          },
        };
      case EntityTyp.Office:
        return {
          modalComponent: OfficeModalComponent,
          entity: !id ? null : await firstValueFrom(this.officeService.officeControllerFindOne({ id })),
          size: "md",
        };
      case EntityTyp.Employee:
        const employeeEntity = !id
          ? null
          : await firstValueFrom(this.employeeService.employeeControllerFindOne({ id }));
        return {
          modalComponent: EmployeeModalComponent,
          entity: employeeEntity,
          size: "md",
          extraData: {
            offices: await firstValueFrom(
              this.officeService.officeControllerFindAllDropdown({
                sIds: employeeEntity?.officeIds ? [...employeeEntity?.officeIds] : undefined,
                initial: true,
              })
            ),
          },
        };
      case EntityTyp.Housing:
        const housingEnitty = !id ? null : await firstValueFrom(this.housingService.housingControllerFindOne({ id }));
        return {
          modalComponent: HousingModalComponent,
          entity: housingEnitty,
          size: "md",
          extraData: {
            offices: await firstValueFrom(
              this.officeService.officeControllerFindAllDropdown({
                sIds: housingEnitty?.officeId ? [housingEnitty?.officeId] : undefined,
                initial: true,
              })
            ),
          },
        };
      case EntityTyp.Client:
        const clientEntity = !id ? null : await firstValueFrom(this.clientService.clientControllerFindOne({ id }));
        return {
          modalComponent: ClientModalComponent,
          entity: clientEntity,
          size: "lg",
          extraData: {
            offices: await firstValueFrom(
              this.officeService.officeControllerFindAllDropdown({
                sIds: clientEntity?.officeId ? [clientEntity?.officeId] : undefined,
                initial: true,
              })
            ),
          },
        };
      case EntityTyp.Report:
        const reportEntity = !id ? null : await firstValueFrom(this.reportService.reportControllerFindOne({ id }));
        return {
          modalComponent: ReportModalComponent,
          entity: reportEntity,
          size: "lg",
          extraData: {
            offices: await firstValueFrom(
              this.officeService.officeControllerFindAllDropdown({
                sIds: reportEntity?.officeId ? [reportEntity?.officeId] : undefined,
                initial: true,
              })
            ),
          },
        };
    }
    return;
  }
}
